import type { BuiltInTool } from './types'

/**
 * #1434: an enterprise's tasks as definitions, for a person's own agent over the API and the OS MCP server: the list
 * over `GET /v1/tasks` and one task in full over `GET /v1/tasks/:id`, both under `tasks:read`. Reading a task's live
 * version is the first half of changing it; the publish that names that version as its base is `os.publish_task_version`.
 *
 * Not `os.list_tasks` and `os.read_task`: those keep their meaning for chat and planning runs, the tasks a member may
 * start. Neither entry lists `run` among its callers, so `servedByOsMcp` is false for both and a task that declares
 * one is refused when it is saved (`tool_not_run_callable`): a task must not read and rewrite itself.
 */
export const OS_TASK_DEFINITION_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.list_task_definitions',
        family: 'os.org',
        summary: "The enterprise's tasks, built-in ones included, a page at a time, each marked builtIn.",
        description:
            "List the enterprise's tasks, a page at a time: the ones it wrote or copied, and the OS's built-in tasks " +
            'every enterprise runs. Each row carries its id, slug, name, kind, status, origin, builtIn (true for a ' +
            'shared built-in definition, false for one of the enterprise’s own), its role and the teams that inherit it ' +
            '(teams: the teams where a member holds that role), currentVersionId ' +
            'and currentVersion (its version number and declared fields). Narrow it with search (slug, name or role), ' +
            'kind, status, origin, teamId, assigneeRole, memberId, name or slug, and sort it by slug, name, kind, ' +
            'status, createdAt or updatedAt. Read one task in full, its skill text included, with ' +
            "os.read_task_definition. The response is { data, total, page, limit, pages }. Never another enterprise's tasks.",
        inputSchema: {
            type: 'object',
            properties: {
                search: { type: 'string', description: 'Only tasks whose slug, name or role contains this text.' },
                kind: { type: 'string', enum: ['llm', 'deterministic'], description: 'Only tasks of this kind.' },
                status: {
                    type: 'string',
                    enum: ['active', 'deprecated'],
                    description: 'Only tasks in this status.',
                },
                origin: {
                    type: 'string',
                    enum: ['tenant', 'os'],
                    description: 'tenant for the enterprise’s own tasks, os for the built-in ones.',
                },
                teamId: {
                    type: 'string',
                    description: 'Only the tasks of the roles held on this team, by a seated member or its lead.',
                },
                assigneeRole: { type: 'string', description: 'Only the tasks that run as this role, by its slug.' },
                memberId: {
                    type: 'string',
                    description: 'Only the tasks of the roles this member holds in the enterprise.',
                },
                name: { type: 'string', description: 'Only tasks whose name contains this text.' },
                slug: { type: 'string', description: 'Only tasks whose slug contains this text.' },
                sort: {
                    type: 'string',
                    enum: ['slug', 'name', 'kind', 'status', 'createdAt', 'updatedAt'],
                    description: 'The field to sort by. Defaults to slug.',
                },
                order: { type: 'string', enum: ['asc', 'desc'], description: 'The sort direction. Defaults to desc.' },
                page: { type: 'integer', minimum: 1, description: 'The page to read, from 1. Defaults to 1.' },
                limit: {
                    type: 'integer',
                    minimum: 1,
                    maximum: 200,
                    description: 'Tasks per page, up to 200. Defaults to 25.',
                },
            },
            additionalProperties: false,
        },
        returns:
            '`{ data, total, page, limit, pages }`, each task with `id`, `slug`, `name`, `kind`, `status`, `origin`, `builtIn`, `currentVersionId` and `currentVersion`.',
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        api: { method: 'GET', path: '/v1/tasks', scope: 'tasks:read' },
        examples: [{ input: { search: 'triage', origin: 'tenant', limit: 10 } }],
        public: true,
    },
    {
        name: 'os.read_task_definition',
        family: 'os.org',
        summary: 'One task in full: its skill text, declared fields, version number and current version id.',
        description:
            "Read one of the enterprise's tasks in full, as it is live now: its skill text (skillMd), its current " +
            'version (currentVersion, with its version number and its declared fields, inputs) and currentVersionId, ' +
            'the identity of that version, which a later change to the task names as the version it builds on, and ' +
            "this enterprise's own values for those inputs (inputValues). It " +
            'also says whether the task is a shared built-in definition (builtIn), and carries its role, the teams ' +
            'that inherit it (teams: the teams where a member holds that role), its predecessors and successors. ' +
            'Pass id, from os.list_task_definitions. Not found for a task in another ' +
            'enterprise.',
        inputSchema: {
            type: 'object',
            properties: {
                id: { type: 'string', description: 'The task, by its id from os.list_task_definitions.' },
            },
            required: ['id'],
            additionalProperties: false,
        },
        returns:
            'The task with `builtIn`, `skillMd`, `currentVersionId` and `currentVersion` (its `version` number and declared `inputs`), and what the portal’s task page shows beside them.',
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [
            {
                code: 'task_not_found',
                when: "`id` names no task in this enterprise, another enterprise's included.",
                handling: 'tool_error',
            },
        ],
        api: { method: 'GET', path: '/v1/tasks/:id', scope: 'tasks:read' },
        examples: [{ input: { id: '5f0c1d2e-0000-4000-8000-000000000013' } }],
        public: true,
    },
]
